import { EventLogRecord } from '@moderepo/mode-apis';
import { fetchEventLogs, selectEventLogs, UIFetchEventLogFilter, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { Entity, EntityMetricsDefinition, isNumericMetricInfo, isTagMetricInfo, MetricInfo, MetricValueType, NumericMetricInfo, SortOrder,
    TagMetricInfo } from '@moderepo/mode-internal-apis';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { TFunction } from 'react-i18next';
import { FetchEntityTimeRangeDataParams, FetchEntityTimeSpanDataParams, getMetricsDefinitionMetricsId, isFetchEntityTimeRangeDataParams,
    isFetchEntityTimeSpanDataParams } from '..';
import { FetchDataByTimeRangeOptions } from '../..';
import { SettingsErrorLogger } from '../../../utils/logger';
import { getDisplayValueAndUnit, isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue,
    sortObjectAttributes } from '../../../utils/utils';


/**
 * Sometimes we need to set an Entity UI Component's color in the uiSettings and we want to use one of the theme colors e.g. info, success,
 * warning, and error. The problem is that we can't use a specific color in the uiSetting because these colors are different for different
 * theme. Therefore, instead of giving them a specific color code, we use the color name. The EntityUIComponent will take care of converting
 * these color to the theme color. For example, we want to use the "success" color for the component's title,
 *
 * Instead of doing this
 *      customSettings: {
 *          titleColor: '#2EFFD9',  // This is the success color for the "MODE Light" theme but what if the user use MODE Dark theme?
 *      }
 *
 * We do this
 *      customSettings: {
 *          titleColor: "success",
 *      }
 */
export enum StateColor {
    INFO = 'info',
    SUCCESS = 'success',
    WARNING = 'warning',
    ERROR = 'error',
    PRIMARY = 'primary',
    SECONDARY = 'secondary'
}


export const COLOR_CODE_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;


/**
 * The set of states an event can have
 */
export enum EventState {
    NEUTRAL = 'neutral',
    GOOD = 'good',
    SERIOUS = 'serious',
    CRITICAL = 'critical'
}


// Give the EventState values so that we can compare them
export const EVENT_STATE_VALUES = Object.freeze({
    [EventState.NEUTRAL] : 1,
    [EventState.GOOD]    : 2,
    [EventState.SERIOUS] : 3,
    [EventState.CRITICAL]: 4,
});


/**
 * This is the interface for mapping some metric value to event state. We will need this to map event field's value to state so that we can
 * dynamically change the value and event card color based on the field's value.
 */
export interface ValueToEventStateMapping {
    readonly [value: string]: EventState | undefined;
}


/**
 * Type guard function to check if an object is ValueToEventStateMapping object
 */
export const isValueToEventStateMapping = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is ValueToEventStateMapping => {
    const settings = obj as ValueToEventStateMapping;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    const stateNames = Object.values(EventState);
    if (!Object.entries(settings).every(([value, stateName]) => {
        // return true if stateName is one of the defined EventState
        if (!isValidSettingStringValue(stateName, true, false, stateNames)) {
            return errorLogger?.logError({
                message: `Value must be one of the following value [${
                    stateNames.map((stateName) => {
                        return `"${stateName}"`;
                    }).join(', ')
                }]`,
                value: stateName,
                path : [...configPath, value],
            }) ?? false;
        }
        return true;
    })) {
        return false;
    }

    return true;
};



/**
 * The interface for 1 filter option. Because this component is generic, it doesn't know anything about the events shown in this component.
 * It doesn't know what fields the events has and what fields can be filtered. therefore, it is up to the admin to configure the filter
 * options to be shown in the UI and how to use them.
 */
export interface EventFilterOption {
    // The metrics definition that this filter apply to. Because the user can select multiple filters and the filters will be combined
    // as 1 and passed to the API, we need to separate the filters by metricsDefinitionId. For example, the user want to show all
    // "Negative" breathalyzer events AND "Hard Brake" events which are from 2 different metrics definition. When we make API call, we won't
    // combine these filters because they are for different metrics definition.
    readonly metricsDefinitionId: string;

    // The translation key to use for the filter option label. If the key does not exist in the translation, it will be displayed as is.
    readonly labelKey: string;

    // The value to filter by. This string should be in the "fieldsFilter" format for the GET log API.
    // For example:
    //      - "speed>60"   // To show all events which has "speed" value greater than 60
    //      - "value===1"  // To show all POSITIVE breathalyzer events
    // An option that has empty string as value will be treated as a divider
    readonly value: string;

    // To hide the option. This can be useful when we want to hide the option temporary and don't want to delete the options from the settings.
    readonly hidden?: boolean | undefined;

    // Whether or not to automatically make this option selected
    readonly selected?: boolean | undefined;
}


/**
 * Type guard function to check if the provided object is an object of type EventFilterOption
 */
export const isEventFilterOption = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EventFilterOption => {
    const settings = obj as EventFilterOption;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.metricsDefinitionId, true)) {
        return errorLogger?.logError({
            message: '"metricsDefinitionId" is required and must a non-empty string.',
            value  : settings.metricsDefinitionId,
            path   : [...configPath, 'metricsDefinitionId'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.labelKey, true)) {
        return errorLogger?.logError({
            message: '"labelKey" is required and must be a non-empty string.',
            value  : settings.labelKey,
            path   : [...configPath, 'labelKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.value, true)) {
        return errorLogger?.logError({
            message: '"value" is required and must be a non-empty string.',
            value  : settings.value,
            path   : [...configPath, 'value'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.hidden)) {
        return errorLogger?.logError({
            message: '"hidden" must be undefined or a boolean.',
            value  : settings.hidden,
            path   : [...configPath, 'hidden'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.selected)) {
        return errorLogger?.logError({
            message: '"selected" must be undefined or a boolean.',
            value  : settings.selected,
            path   : [...configPath, 'selected'],
        }) ?? false;
    }

    return true;
};



/**
 * When we configure the metrics to show in the event UI component, we need to provide some info about the metrics so that the component
 * know how to find these metrics from the metricsDefinition. This is the interface for the metrics settings.
 */
export interface EventMetricSettings {
    // The name of the metric, this can be one of the names in the metricsDefinition's numericMetrics or tagMetrics
    readonly name: string;

    // Metric's displayName is defined in the metrics definition. Sometimes, we want to override this name and show something else therefore,
    // this attribute can be used to override the metric's displayName. This can be a translation key but the admin must make sure the key
    // is added to the project's "translationsOverride" or else the key will be displayed as is.
    readonly displayNameKey?: string | undefined;

    // It is possible for numericMetrics and tagMetrics to have metrics with the same name. This option tells the component whether to
    // find the metric with that name specified above from 'numericMetrics' or 'tagMetrics' array. This is OPTIONAL and only needed when
    // we have numeric/tag metrics with the same name. If this is NOT provided then the component will try to find the metric from
    // 'numericMetrics' first and then 'tagMetrics'.
    readonly valueType?: MetricValueType | undefined;

    // The number of decimal places to round the numeric metric value to. This is used for numeric metrics
    readonly decimalPlaces?: number | undefined;

    /**
     * tagMetrics' values can have 'enumDefinitions' which can be used to convert internal values to user-friendly display values. However,
     * sometimes we need to override the 'enumDefinitions' values and use customized display values instead. We don't want to change the
     * 'enumDefinitions' in the entityClass configuration because that would change the display everywhere. Sometimes we just want to override
     * the display value in 1 place therefore this is the mapping to override the 'enumDefinitions'.
     * NOTE: This can also be used for numericMetrics however, it works better with integer numeric metric values.
     */
    readonly valueToDisplayValueMapping?: {
        readonly [value: string]: string | undefined;
    } | undefined;

    /**
     * Some metrics have special meaning to the event. Their value can be used to determine the event's state, bad, good, etc... This component
     * does not know what the metric value means therefore, it is up to the admin to configure it. An event can have 3 different states (4 if
     * we include none as another state). The admin need to tell the component which value mean which state e.g.
     *
     *      valueToEventStateMapping: {
     *          0: "good",          // For this metric, value "0" means Good state
     *          9: "serious"        // value "9" means bad state but not the worst
     *          1: "critical",      // and value "1" means the worst state
     *      }
     *
     * Then, the component will highlight the metric value base on this config. If the metric value mapped to a "good" state in this config then
     * the value will be highlighted in Green/Success color. If the metric value mapped to a "serious" state, the value will be highlighted in
     * Orange/Warning color. And if the value is mapped to the "critical" state then the value will be highlighted in Red/Error color.
     */
    readonly valueToEventStateMapping?: ValueToEventStateMapping | undefined;

    /**
     * When a metric has no value, the component will use the DEFAULT no value setting to display the value for the metric e.g."--". However,
     * some metrics might requires customized display instead of the default string. This attribute allows the admin to configure custom string
     * to use for each specific metric. For example, if we want to show "Not Taken" for the breathalyzer "result" metric, we can set this attribute
     * to "Not Taken".
     */
    readonly noValueDisplayKey?: string | undefined;

    /**
     * Similar to "defaultNoValueState" but this is used when we want a metric to have custom state when it has no value. For example, we have a
     * metric which having no value means GOOD then we will need to specify it using this attribute. This will override the "defaultNoValueState"
     * for specific metrics. For example, for Vehicle events, having NO "event_type" means good.
     *      noValueState: "good"
     */
    readonly noValueState?: EventState | undefined;
}



/**
* Type guard function to check if an object is a type of EventMetricSettings
*/
export const isEventMetricSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EventMetricSettings => {

    const settings = obj as EventMetricSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.name, true)) {
        return errorLogger?.logError({
            message: '"name" is required and must be a non-empty string',
            value  : settings.name,
            path   : [...configPath, 'name'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.displayNameKey)) {
        return errorLogger?.logError({
            message: '"displayNameKey" must be undefined or a non-empty string',
            value  : settings.displayNameKey,
            path   : [...configPath, 'displayNameKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.valueType, false, false, Object.values(MetricValueType))) {
        return errorLogger?.logError({
            message: `"valueType" must be undefined or one of the following [${
                Object.values(MetricValueType).map((type) => {
                    return `"${type}"`;
                }).join(', ')
            }]`,
            value: settings.valueType,
            path : [...configPath, 'valueType'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.decimalPlaces, false, false, 0, 10)) {
        return errorLogger?.logError({
            message: '"decimalPlaces" must be undefined or an integer between 0 and 10 inclusive',
            value  : settings.decimalPlaces,
            path   : [...configPath, 'decimalPlaces'],
        }) ?? false;
    }

    if (settings.valueToDisplayValueMapping !== undefined) {
        if (!isObjectLiteral(settings.valueToDisplayValueMapping)) {
            return errorLogger?.logError({
                message: '"valueToDisplayValueMapping" must be undefined or an Objects.',
                value  : settings.valueToDisplayValueMapping,
                path   : [...configPath, 'valueToDisplayValueMapping'],
            }) ?? false;
        }

        // EVERY values in the "valueToDisplayValueMapping" must be string
        if (!Object.entries(settings.valueToDisplayValueMapping).every(([value, displayValue]) => {
            if (!isValidSettingStringValue(displayValue, true)) {
                return errorLogger?.logError({
                    message: 'Every values in "valueToDisplayValueMapping" must be non-empty string',
                    value  : displayValue,
                    path   : [...configPath, 'valueToDisplayValueMapping', value],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    if (
        settings.valueToEventStateMapping !== undefined
        && !isValueToEventStateMapping(settings.valueToEventStateMapping, errorLogger, [...configPath, 'valueToEventStateMapping'])
    ) {
        return false;
    }

    if (!isValidSettingStringValue(settings.noValueDisplayKey)) {
        return errorLogger?.logError({
            message: '"noValueDisplayKey" must be undefined or a non-empty string',
            value  : settings.noValueDisplayKey,
            path   : [...configPath, 'noValueDisplayKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.noValueState, false, false, Object.values(EventState))) {
        return errorLogger?.logError({
            message: `"noValueState" must be undefined or one of the following values [${
                Object.values(EventState).map((type) => {
                    return `"${type}"`;
                }).join(', ')
            }]`,
            value: settings.noValueState,
            path : [...configPath, 'noValueState'],
        }) ?? false;
    }

    return true;
};


/**
 * Given the metricsDefinition and an object containing the information of the metric we want to show, find and return the metric info object
 * from the metricsDefinition, the NumericMetricInfo or TagMetricUInfo.
 */
export const findMetricInfo = (
    metricsDefinition: EntityMetricsDefinition,
    metricInfoSettings: EventMetricSettings,
): NumericMetricInfo | TagMetricInfo | undefined => {

    // If the "valueType" is specified as "number" or not specified then try to find the metric from the "numericMetrics" array
    const numericMetric = !metricInfoSettings.valueType || metricInfoSettings.valueType === MetricValueType.NUMBER
        ? metricsDefinition?.numericMetrics?.find((metricInfo) => {
            return metricInfo.name === metricInfoSettings.name;
        })
        : undefined;

    // If the "valueType" is specified as "string" or not specified then try to find the metric from the "tagMetrics" array
    const tagMetric = !metricInfoSettings.valueType || metricInfoSettings.valueType === MetricValueType.STRING
        ? metricsDefinition?.tagMetrics?.find((metricInfo) => {
            return metricInfo.name === metricInfoSettings.name;
        })
        : undefined;

    // Return the either tha numericMetric or tagMetric that we found
    return numericMetric ?? tagMetric;
};



/**
 * Given a MetricInfo and ShowEventMetricInfoSettings, return the metric's 'displayName'. MetricInfo object already has displayName so we
 * can use that HOWEVER, sometimes we want to customize the metric's display name using the 'metricInfoSettings' so we need to check this
 * 'metricInfoSettings' first. If there is a custom display name configured then use that name instead of the metric's displayName.
 */
export const getEventMetricDisplayName = (
    metric: MetricInfo,
    metricInfoSettings: EventMetricSettings,
    trans: TFunction,
): string => {
    // If the 'metricInfoSettings' contains 'displayNameKey' then use that displayNameKey
    if (metricInfoSettings.displayNameKey) {
        return trans(metricInfoSettings.displayNameKey);
    }

    // Else, use the displayName defined in the MetricInfo object. For NUMERIC Metric info object, the displayName can come from the
    // metric.evaluator.
    return isNumericMetricInfo(metric)
        ? metric.evaluator?.displayName ?? metric.displayName ?? metric.name
        : metric?.displayName ?? metric.name;
};



/**
 * Given a value from an event record's field, return a stringified value of the value.
 * This function will try to format the value if there are format settings
 */
export const getEventMetricDisplayValue = (
    value: unknown,
    metric: MetricInfo,
    metricInfoSettings: EventMetricSettings,
    trans: TFunction,
): string => {
    if (typeof value === 'number') {
        const unit = metric && isNumericMetricInfo(metric) ? metric.unit : undefined;

        // If the metric has "valueToDisplayValueMapping" settings, see if we can find a mapping for the "value". If there is one, return the
        // string value from the "valueToDisplayValueMapping" that is mapped to "value"
        const customDisplayValue = metricInfoSettings.valueToDisplayValueMapping?.[value];
        if (customDisplayValue !== undefined) {
            return trans(customDisplayValue);
        }

        // If there is no mapping for the value and if there is a "decimalPlaces" settings, format th value
        const formattedValue = metricInfoSettings.decimalPlaces !== undefined
            ? trans('{{value, number-format}}', { value, decimalPlaces: metricInfoSettings.decimalPlaces })
            : value;

        return getDisplayValueAndUnit(formattedValue.toString(), unit, trans);
    }

    if (typeof value === 'string') {
        // If there is an "valueToDisplayValueMapping" configured for the "value" then use that custom value.
        const customDisplayValue = metricInfoSettings.valueToDisplayValueMapping?.[value];
        if (customDisplayValue !== undefined) {
            return trans(customDisplayValue);
        }

        // If metric is TAG METRIC, see if there is a displayValue from the tagMetric.enumDefinition. If there is, use the
        // enum display value. Else, return the value as is
        if (isTagMetricInfo(metric)) {
            return metric.enumDefinitions?.find((enumDef) => {
                return enumDef.value === value;
            })?.name ?? value;
        }
    }

    // If there is no special settings for formatting the value then return the value as string.
    return JSON.stringify(value);
};



/**
 * Helper function to create a unique metric name. It is possible that multiple collection having the same numeric/tag metric name therefore
 * we need to add collectionId and metric type 'numeric' or 'tag' to the metric names to make them unique. When we merge data from multiple
 * collections into 1 data object, the metric name must be unique or else there will be conflict
 */
export const createUniqueMetricId = (collectionId: string, metricName: string, isNumericMetric: boolean): string => {
    if (isNumericMetric) {
        return `${collectionId}-numeric-${metricName}`;
    }
    return `${collectionId}-tag-${metricName}`;
};


// Temporary Interface for storing params required for fetching event log data
export interface FetchMissingEventLogDataParams {
    readonly homeId: number;
    readonly logResourcePath: string;
    readonly filters: UIFetchEventLogFilter;
}



/**
 * Fetch and return an array of event logs, SORTED by timestamp, for the given params.
 */
export const useGetEventLogData = (
    homeId: number | undefined,
    entities: readonly Entity[] | undefined,
    metricsDefinitions: readonly EntityMetricsDefinition[] | undefined,
    startTime: number | undefined,
    endTime: number | undefined,
    limit: number,
    sortOrder: SortOrder | undefined,
    upToPageNumber: number,
    filterOptions: readonly EventFilterOption[] | undefined,
    defaultFiltersPerMetricsDef: {
        readonly [metricsDefId: string]: string | undefined,
    } | undefined,
): {
    eventLogRecords: readonly EventLogRecord[];
    hasMoreRecords: boolean;
} => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);

    const [paramsForFetchingMissingData, setParamsForFetchingMissingData] = useState<readonly FetchMissingEventLogDataParams[] | undefined>();


    /**
     * Get all the necessary event records from the data state and return the sorted event records.
     * This function will get and return all event records for all entities, metrics definition, and pages. It will only get and return data from
     * cache. For any data that is not fetched, it will return an array of actions to fetch them.
     * This function will also return a map of hasMoreRecords for each metrics definition so that we can return it to the caller to let the
     * caller knows whether or not there are more data to fetch based on the current page.
     */
    const {
        eventLogRecords,
        hasMoreRecordsPerMetricsDefinition,
    } = useMemo((): {
        readonly eventLogRecords: readonly EventLogRecord[] | undefined,
        readonly hasMoreRecordsPerMetricsDefinition: {
            [metricsDefId: string]: boolean;
        } | undefined,
        readonly fetchDataActionsForMissingData: readonly FetchMissingEventLogDataParams[] | undefined,
    } => {

        if (metricsDefinitions && homeId !== undefined && startTime !== undefined && endTime !== undefined && entities) {

            // We will go try to select data from the data state and if some data are NOT fetched, we need to remember to fetch them.
            // However, we will not fetch the missing data here. We will only store the params needed to fetch missing data. There will
            // be another function that handle fetching missing data based on these params
            const newParamsForFetchingMissingData: FetchMissingEventLogDataParams [] = [];

            // For each metrics definition, we will need to make 1 API call and each of them can have different pagination. It is possible that
            // 1 API call to fetch a metrics definition data return all the records while another API call return partial records. Therefore,
            // we need to keep track of where there are still more records to load for each metrics definition separately
            const hasMoreRecordsPerMetricsDefinition: {
                [metricsDefId: string]: boolean;
            } = {};

            // All the log records we have fetched will be stored here. This will be returned to the caller.
            const allLogRecords: EventLogRecord[] = [];


            metricsDefinitions.forEach((metricsDef) => {

                const metricsDefinitionId = metricsDef.metricsDefinitionId;
                const logResourcePath = metricsDef.dataStoreConfiguration.moduleInstanceId;

                // Each searchKey is a combination of entity + metricsDefinitionId. To fetch data for multiple entities, we need to combine
                // the search keys for all the entities separated by commas
                const searchKeys = entities.map((entity) => {
                    return getMetricsDefinitionMetricsId(entity, metricsDefinitionId);
                }).join(',');

                // "filterOptions" contain filters for ALL metrics definition therefore, we need to filter out filterOptions that are not
                // configured for the current metrics definition
                const applicableFilters = filterOptions?.filter((filter) => {
                    return filter.metricsDefinitionId === metricsDefinitionId;
                });

                // If there is at least 1 filters in "applicableFilters" array, combine all the `applicableFilters` and join them with
                // "OR" conjunction operator. If there is no applicableFilters, use the "defaultFilters" if it is available
                const fieldFilters = applicableFilters && applicableFilters.length > 0 ? `OR(${applicableFilters.map((filterOption) => {
                    return filterOption.value;
                }).join(',')})` : defaultFiltersPerMetricsDef?.[metricsDefinitionId];


                /**
                 * Only need to select data IF no filters were selected (the user want to see all data) OR the user selected some filters
                 * AND there are at least 1 filters applicable for this metrics definition. If the user selected filters that are NOT applicable
                 * for this metrics definition then we don't need to show data from this metrics definition.
                 */
                if (!filterOptions || filterOptions.length === 0 || (applicableFilters && applicableFilters.length > 0)) {
                    for (let pageNumber = 0; pageNumber <= upToPageNumber; pageNumber += 1) {

                        const filters: UIFetchEventLogFilter = {
                            begin       : (new Date(startTime)).toISOString(),
                            end         : (new Date(endTime)).toISOString(),
                            sortBy      : sortOrder,
                            pageSize    : limit,
                            pageNumber,
                            searchKeys,
                            fieldsFilter: fieldFilters,
                        };

                        // Try to get cached data from the data state. If the data is already in cache, use it. If not, create an action to fetch
                        // it.
                        const cachedData = selectEventLogs(appDataState, homeId, logResourcePath, filters);
                        if (cachedData && cachedData.records) {
                            allLogRecords.push(...cachedData.records);
                        } else if (!cachedData && hasMoreRecordsPerMetricsDefinition[metricsDefinitionId] !== false) {
                            // Create an action to fetch data if we don't have data in cache AND if "hasMoreRecords"
                            // is NOT specifically set to false for the metrics definition. If we can't find data in cache BUT it is is because
                            // there are no more records then don't need to fetch data
                            newParamsForFetchingMissingData.push({
                                homeId,
                                logResourcePath,
                                filters,
                            });
                        }

                        // Update the "hasMoreRecords" for the current metrics definition.
                        if (cachedData) {
                            hasMoreRecordsPerMetricsDefinition[metricsDefinitionId] = cachedData.skip + limit < cachedData.total;
                        }
                    }
                }
            });


            // Set the newParamsForFetchingMissingData to remember that we need to fetch these missing data. But we only set it if the
            // newParamsForFetchingMissingData is different from the current paramsForFetchingMissingData
            setParamsForFetchingMissingData((currentActions) => {
                if (
                    !currentActions
                    || JSON.stringify(sortObjectAttributes(currentActions)) !== JSON.stringify(sortObjectAttributes(newParamsForFetchingMissingData))
                ) {
                    return newParamsForFetchingMissingData;
                }
                return currentActions;
            });


            /**
             * Sort the records based on the provided sortOrder and return the sorted records. Even though the records returned by the back end
             * are sorted, we have to sort them again because we make MULTIPLE API calls to fetch the records and merged the records into 1
             * array therefore, the records are no longer sorted.
             * NOTE: we only need to sort the records if we made multiple API calls, when there are more than 1 metricsDefinitions. If not then
             * we can just return the logRecords array as is because it is already sorted by the back end.
             */
            if (metricsDefinitions.length > 1) {

                // Sort the records and return the sorted records
                const sortedRecords = allLogRecords.sort((record1, record2) => {
                    const date1 = moment(record1.timestamp);
                    const date2 = moment(record2.timestamp);

                    // result will be the records sorted in ASC order
                    const result = date1.valueOf() - date2.valueOf();

                    // If the sortOrder is DESC then we multiply the result by -1 to reverse the order.
                    return sortOrder === SortOrder.DESC ? result * -1 : result;
                });

                return {
                    eventLogRecords               : sortedRecords,
                    hasMoreRecordsPerMetricsDefinition,
                    fetchDataActionsForMissingData: newParamsForFetchingMissingData,
                };
            }


            // Return the records array as is because they should already been sorted.
            return {
                eventLogRecords               : allLogRecords,
                hasMoreRecordsPerMetricsDefinition,
                fetchDataActionsForMissingData: newParamsForFetchingMissingData,
            };
        }

        return {
            eventLogRecords                   : undefined,
            hasMoreRecordsPerMetricsDefinition: undefined,
            fetchDataActionsForMissingData    : undefined,
        };
    }, [appDataState, defaultFiltersPerMetricsDef, endTime, entities, filterOptions, homeId, limit, metricsDefinitions, upToPageNumber, sortOrder,
        startTime]);


    /**
     * This useEffect is used for checking the list of "paramsForFetchingMissingData" to see if there are any actions need to be dispatched
     * and dispatch them.
     */
    useEffect(() => {
        if (paramsForFetchingMissingData && paramsForFetchingMissingData.length > 0) {
            const actions = paramsForFetchingMissingData;
            actions.forEach((action) => {
                appDataDispatch(fetchEventLogs(action.homeId, action.logResourcePath, action.filters)).then(() => {
                    // ignore errors
                });
            });
        }
    }, [appDataDispatch, paramsForFetchingMissingData]);



    /**
     * Aggregate all the hasMoreRecords per metrics definition and return TRUE if at least one of the metrics definition still have more data
     */
    const hasMoreRecords = useMemo(() => {
        return hasMoreRecordsPerMetricsDefinition && Object.values(hasMoreRecordsPerMetricsDefinition).some((hasMoreData) => {
            return hasMoreData;
        });
    }, [hasMoreRecordsPerMetricsDefinition]);


    return {
        eventLogRecords: eventLogRecords ?? [],
        hasMoreRecords : Boolean(hasMoreRecords),
    };
};



/**
 * Many EntityUIComponent takes in time control's fetchDataParams and then convert them to FetchDataByTimeRangeOptions so that it is easier
 * to use. Therefore, we will make this a reusable custom hook that those EntityUIComponent can use.
 */
export const useConvertTimeControlParamsToFetchDataTimeRangeParams = (
    fetchDataParams: FetchEntityTimeSpanDataParams | FetchEntityTimeRangeDataParams | undefined,
): FetchDataByTimeRangeOptions => {

    /**
     * Create the params that are required for fetching data based on the fetchDataParams. This function will convert the fetchDataParams
     * to FetchDataByTimeRangeOptions.
     */
    const result = useMemo((): FetchDataByTimeRangeOptions => {
        if (isFetchEntityTimeRangeDataParams(fetchDataParams)) {
            // FetchEntityTimeRangeDataParams has the EXACT attribute as FetchDataByTimeRangeOptions so we can make a copy of the params
            return {
                ...fetchDataParams,
            };
        }

        if (isFetchEntityTimeSpanDataParams(fetchDataParams)) {
            // If fetchDataParams is a TimeSpanDataParams which has 2 attributes, timestamps (array) and timeSpan, we will convert
            // it to FetchDataByTimeRangeOptions. We will use the FIRST timestamp in the timestamps array as startTime and add the timeSpan
            // to the startTime to get the endTime. For example, if timeSpan = "1 day", we add 24 hours to the startTime. If timeSpan = "1 week",
            // we add 7*24 hours to the startTime to get the endTime.
            const startTime = fetchDataParams.timestamps[0];
            const endTime = moment(startTime).add(1, fetchDataParams.timeSpan).valueOf();
            return {
                startTime,
                endTime,
            };
        }


        // If fetchDataParams is not provided or not a known fetch data params then we will return use the default range, 1 day.
        const now = moment();
        return {
            startTime: moment(now).subtract(1, 'day').valueOf(),
            endTime  : now.valueOf(),
        };
    }, [fetchDataParams]);

    return result;
};
